import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './styles.scss';
import 'react-image-crop/dist/ReactCrop.css';
import { Storage } from 'aws-amplify';
import { Alert } from '@aws-amplify/ui-react';
import ReactTooltip from 'react-tooltip';
import { trackEventIfConsented } from './analyticsConsent';
import { trackInternalNavigationIfConsented } from './pageAnalytics';
import {
  AMAZON_STORE_PREFERENCE_KEY,
  AMAZON_STORE_UPDATED_EVENT,
  buildAmazonSearchUrl,
  getAmazonStoreResolution,
} from './amazonAffiliate';

const SITE_URL = 'https://www.101setups.com';
const INTERNAL_NAV_SOURCE_PAGE_GROUPS = {
  home_feed: 'home',
  latest_feed: 'latest_page',
  search_hub: 'search_hub',
  topic_hub: 'topic_hub',
  setup_page: 'setup_page',
  setup_related: 'setup_page',
};

const getInitialAmazonStoreState = () => {
  const resolution = getAmazonStoreResolution(null);

  return {
    region: resolution.region,
    source: resolution.source,
  };
};

const toAbsoluteShareUrl = sharePath => {
  if (!sharePath) {
    return SITE_URL;
  }

  return /^https?:\/\//i.test(sharePath) ? sharePath : `${SITE_URL}${sharePath}`;
};

const Post = ({
  id,
  userID,
  imgURL,
  listTags = [],
  title,
  summary,
  categoryLabel,
  canonicalPath,
  sharePath,
  altText,
  showViewLink = true,
  pageContext = 'unknown',
}) => {
  const [url, setUrl] = useState('http://www.amazon.co.uk');
  const [showAlert, setShowAlert] = useState(false);
  const [amazonStoreState, setAmazonStoreState] = useState(getInitialAmazonStoreState);
  const creatorName = useMemo(() => String(userID || '').replace(/^"+|"+$/g, '') || 'community', [userID]);
  const viewPath = canonicalPath || `/shared/${id}`;
  const copyTarget = toAbsoluteShareUrl(sharePath || viewPath);
  const sourcePageGroup = INTERNAL_NAV_SOURCE_PAGE_GROUPS[pageContext] || pageContext || 'unknown';

  const syncAmazonStoreState = () => {
    const resolution = getAmazonStoreResolution(null);
    setAmazonStoreState({
      region: resolution.region,
      source: resolution.source,
    });
  };

  const handleViewSetupClick = () => {
    trackInternalNavigationIfConsented({
      link_target: viewPath,
      link_context: 'post_card_view_setup',
      source_page_group: sourcePageGroup,
      destination_page_group: 'setup_page',
      post_id: id,
    });
  };

  const copyURL = async () => {
    try {
      await navigator.clipboard.writeText(copyTarget);
      setShowAlert(true);
      trackEventIfConsented('share_post_click', {
        post_id: id,
        page_context: pageContext,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const openAmazonSearch = (targetUrl, itemName) => {
    trackEventIfConsented('amazon_search_click', {
      post_id: id,
      item_name: itemName || title || 'Desk setup item',
      store_region: amazonStoreState.region,
      page_context: pageContext,
    });
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  };

  const renderTooltipContent = dataTip => {
    ReactTooltip.rebuild();

    if (!dataTip) {
      return '';
    }

    const [name, amazonUrl] = dataTip.split('|');

    return name ? (
      <div className="tooltip-card">
        <h3 className="tooltip-card-title">{name}</h3>
        <div className="tooltip-card-actions">
          <button className="buyButton" onClick={() => openAmazonSearch(amazonUrl, name)}>
            Search on Amazon
          </button>
        </div>
      </div>
    ) : null;
  };

  useEffect(() => {
    async function loadURL() {
      async function getURL({ key }) {
        try {
          const resolvedUrl = await Storage.get(key, {
            level: 'public',
          });
          return resolvedUrl;
        } catch (error) {
          console.log(error);
        }

        return null;
      }

      try {
        const keyValue = JSON.parse(imgURL);
        const resolvedImageUrl = await getURL(keyValue);

        if (resolvedImageUrl) {
          setUrl(resolvedImageUrl);
        }
      } catch (error) {
        console.log(error);
      }
    }

    loadURL();
  }, [imgURL]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleStoreUpdated = () => {
      syncAmazonStoreState();
    };

    const handleStorage = event => {
      if (event.key === AMAZON_STORE_PREFERENCE_KEY) {
        syncAmazonStoreState();
      }
    };

    window.addEventListener(AMAZON_STORE_UPDATED_EVENT, handleStoreUpdated);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(AMAZON_STORE_UPDATED_EVENT, handleStoreUpdated);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  return (
    <article className="single-post">
      <header className="post-card-header">
        {categoryLabel ? <p className="post-eyebrow">{categoryLabel}</p> : null}
        {title ? (
          <h2 className="post-title">
            <Link to={viewPath} onClick={handleViewSetupClick}>
              {title}
            </Link>
          </h2>
        ) : (
          <span className="userpost">{creatorName}</span>
        )}
        <p className="post-meta-line">Shared by {creatorName}</p>
        {summary ? <p className="post-summary">{summary}</p> : null}
      </header>

      <div className="image" tabIndex="0">
        <div className="ReactCropp">
          <img src={url} alt={altText || title || 'Desk setup'} />
          {listTags.map((tag, tagIndex) => {
            const amazonUrl = buildAmazonSearchUrl({
              region: amazonStoreState.region,
              keywords: tag.tag.name,
            });

            return (
              <div
                className="crop-item"
                key={tagIndex}
                data-for="label"
                data-tip={`${tag.tag.name}|${amazonUrl}`}
                data-event={'click focus'}
                clickable={true}
                html={true}
                style={{
                  unit: tag.tag.unit,
                  width: `${tag.tag.width}%`,
                  height: `${tag.tag.height}%`,
                  left: `${tag.tag.x}%`,
                  top: `${tag.tag.y}%`,
                }}
              ></div>
            );
          })}
        </div>
        <ReactTooltip className="rtt" place="bottom" effect="solid" id="label" multiline={true} getContent={renderTooltipContent} />
      </div>

      <footer>
        <div className="list">
          <div className="interaction post-actions-row">
            {showViewLink ? (
              <Link className="inline-view-link" to={viewPath} onClick={handleViewSetupClick}>
                View setup
              </Link>
            ) : null}
            <button className="share" onClick={copyURL}>
              Copy link
            </button>
            {showAlert ? (
              <Alert variation="success" isDismissible={true} hasIcon={false} heading="Link copied to clipboard">
                You can now paste this setup link anywhere.
              </Alert>
            ) : null}
          </div>
          <ul className="properList">
            {listTags.map((tag, tagIndex) => (
              <li className="listofItems" key={tagIndex}>
                <span>{tag.tag.name}</span>
              </li>
            ))}
          </ul>
        </div>
      </footer>
    </article>
  );
};

export default Post;
